import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../supabase.js";
import GenerateModal from "./ContentGeneration/GenerateModal.js";
import ProjectSettingsModal from "./ProjectSettingsModal.jsx";
import {
  getArticle,
  deleteArticle,
  updateArticle,
  getDisplayStatus,
} from "../lib/articles.js";
import { generateContent } from "./ContentGeneration/generateContent.js";

function GeneratingOverlay({ message }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "#111",
          color: "#fff",
          borderRadius: 12,
          padding: "22px 26px",
          width: "min(520px, 92vw)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          whiteSpace: "pre-line",
          textAlign: "center",
          fontSize: 18,
          lineHeight: 1.35,
        }}
      >
        {message}
      </div>
    </div>
  );
}

// ----- Small icons -----
const DotsIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 6.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM12 20.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
      fill="#111827"
    />
  </svg>
);

const SearchIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
      stroke="#6b7280"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ----- Donut chart (simple SVG) -----
const DonutBreakdown = ({ breakdown, size = 96, stroke = 12 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  // If no values, show empty ring
  const total = breakdown.reduce((sum, b) => sum + (b.value || 0), 0);
  if (!total) {
    return (
      <svg width={size} height={size} style={{ display: "block" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#e5e7eb"
          strokeWidth={stroke}
          fill="none"
        />
      </svg>
    );
  }

  let offset = 0;

  return (
    <svg width={size} height={size} style={{ display: "block" }}>
      {/* Background ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="#e5e7eb"
        strokeWidth={stroke}
        fill="none"
      />

      {/* Segments */}
      {breakdown.map((b) => {
        const pct = (b.value / total) * 100;
        const dash = (pct / 100) * c;

        const segment = (
          <circle
            key={b.label}
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={b.color}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="butt"
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        );

        offset += dash;
        return segment;
      })}
    </svg>
  );
};

// ---- helpers ----
const normalizeUserName = (u) =>
  u?.full_name ||
  u?.name ||
  u?.email ||
  (u?.id ? String(u.id).slice(0, 8) : "User");

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

// Map your article progress values into the “health” buckets shown on UI
const healthBucket = (progress) => {
  const p = (progress || "").toLowerCase().trim();

  if (p.includes("ready to publish")) return "Ready to Publish";
  if (p.includes("ready for approval")) return "Ready for Approval";
  if (p.includes("ready for review")) return "Ready for Review";
  if (p.includes("edits needed")) return "Edits Needed";
  if (p.includes("in progress")) return "In Progress";

  // fallback: if it's something unexpected, keep it visible
  return "In Progress";
};

export default function ProjectDetailPage({ profile, setFeedback, organization }) {
  const { projectId } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [project, setProject] = useState(null);
  const [team, setTeam] = useState([]); // {id,name,role, avatar_url}
  const [articles, setArticles] = useState([]); // rows from articles

  // UI filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [writerFilter, setWriterFilter] = useState("");

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState("");

  const navigate = useNavigate();

  const [openMenuId, setOpenMenuId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const projectAsOption = project
    ? [{ id: project.id, title: project.title }]
    : [];

  const buildTeamFromProject = async (proj) => {
    if (!proj) return;

    const ids = Array.from(
      new Set(
        [
          proj.created_by,
          ...(proj.writers || []),
          ...(proj.editors || []),
          ...(proj.approvers || []),
        ].filter(Boolean)
      )
    );

    if (ids.length === 0) {
      setTeam([]);
      return;
    }

    // ✅ Added avatar_url to selection
    const { data: users, error: usersErr } = await supabase
      .from("Users")
      .select("id,name,email,avatar_url")
      .in("id", ids);

    if (usersErr) throw usersErr;

    const byId = new Map((users || []).map((u) => [u.id, u]));

    const lead = proj.created_by
      ? {
          id: proj.created_by,
          name: normalizeUserName(byId.get(proj.created_by)),
          avatar_url: byId.get(proj.created_by)?.avatar_url,
          role: "Project Lead",
        }
      : null;

    const writersList = (proj.writers || []).map((id) => ({
      id,
      name: normalizeUserName(byId.get(id)),
      avatar_url: byId.get(id)?.avatar_url,
      role: "Writer",
    }));

    const editorsList = (proj.editors || []).map((id) => ({
      id,
      name: normalizeUserName(byId.get(id)),
      avatar_url: byId.get(id)?.avatar_url,
      role: "Editor",
    }));

    const approversList = (proj.approvers || []).map((id) => ({
      id,
      name: normalizeUserName(byId.get(id)),
      avatar_url: byId.get(id)?.avatar_url,
      role: "Approver",
    }));

    const merged = [
      lead,
      ...approversList,
      ...editorsList,
      ...writersList,
    ].filter(Boolean);

    // remove duplicates (same person + same role)
    const unique = [];
    const seen = new Set();
    for (const m of merged) {
      const key = `${m.id}:${m.role}`;
      if (!seen.has(key)) {
        unique.push(m);
        seen.add(key);
      }
    }

    setTeam(unique);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setLoadError("");

        // 1) Load project
        const { data: proj, error: projErr } = await supabase
          .from("projects")
          .select(
            "id,title,organization,description,company_id,created_by,writers,editors,approvers,created_at,updated_at"
          )
          .eq("id", projectId)
          .eq("company_id", profile.company_id) // ✅ ADDED SECURITY
          .single();

        if (projErr) throw projErr;
        if (cancelled) return;

        setProject(proj);

        // 2) Load articles assigned to this project
        const { data: arts, error: artsErr } = await supabase
          .from("articles")
          .select(
            `
            id,
            org_id,
            title,
            created_by,
            created_at,
            updated_at,
            progress,
            project_id,
            meta_tags,
            content_html,
            Users:created_by ( name )
            `
          )
          .eq("project_id", projectId)
          .eq("org_id", profile.company_id) // ✅ ADDED SECURITY
          .order("updated_at", { ascending: false });

        if (artsErr) throw artsErr;
        if (cancelled) return;

        setArticles(arts || []);

        await buildTeamFromProject(proj);
      } catch (e) {
        console.error(e);
        setLoadError(e?.message || "Failed to load project.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!project?.id) return;

    let cancelled = false;

    (async () => {
      try {
        await buildTeamFromProject(project);
      } catch (e) {
        if (!cancelled) console.error("Failed to rebuild team:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // key fields that affect team membership
  }, [
    project?.id,
    project?.created_by,
    (project?.writers || []).join(","),
    (project?.editors || []).join(","),
    (project?.approvers || []).join(","),
  ]);

  // Build “Project Articles table rows”
  const writerNameById = useMemo(() => {
    // We can derive writer names from Users table later by joining.
    // For now: if the author is in team list, use it; else show short id.
    const map = new Map();
    for (const m of team) map.set(m.id, m.name);
    return map;
  }, [team]);

  const tableRows = useMemo(() => {
    const rows = (articles || []).map((a) => ({
      ...a,
      // ✅ CHANGE: Use the name from the join if available
      writerName:
        a.Users?.name ||
        writerNameById.get(a.created_by) ||
        (a.created_by ? String(a.created_by).slice(0, 8) : "—"),
      score: null, // you don’t have score column in schema yet
      statusLabel: getDisplayStatus(a.progress, organization?.ai_enabled),
      updatedLabel: fmtDate(a.updated_at),
    }));

    return rows.filter((r) => {
      const matchesSearch = r.title
        ?.toLowerCase()
        .includes(search.toLowerCase());
      const matchesStatus = statusFilter
        ? r.statusLabel === statusFilter
        : true;
      const matchesWriter = writerFilter ? r.writerName === writerFilter : true;
      return matchesSearch && matchesStatus && matchesWriter;
    });
  }, [
    articles,
    writerNameById,
    search,
    statusFilter,
    writerFilter,
    organization,
  ]);

  // Stats + health
  const stats = useMemo(() => {
    const total = articles?.length || 0;

    const published = (articles || []).filter(
      (a) =>
        healthBucket(getDisplayStatus(a.progress, organization?.ai_enabled)) ===
        "Published"
    ).length;
    const inProgress = (articles || []).filter(
      (a) =>
        healthBucket(getDisplayStatus(a.progress, organization?.ai_enabled)) ===
        "In Progress"
    ).length;

    // avgScore: schema doesn’t include one. Keep placeholder for now.
    return { total, published, inProgress, avgScore: "—" };
  }, [articles, organization]);

  const health = useMemo(() => {
    const buckets = organization?.ai_enabled
      ? [
          { label: "In Progress", color: "#98fb98" },
          { label: "Ready for Review", color: "#48a860" },
          { label: "Edits Needed", color: "#2e8b57" },
          { label: "Ready for Approval", color: "#3f704d" },
          { label: "Ready to Publish", color: "#1a2421" },
        ]
      : [
          { label: "In Progress", color: "#98fb98" },
          { label: "Ready for Approval", color: "#3f704d" },
          { label: "Ready to Publish", color: "#1a2421" },
        ];

    const counts = Object.fromEntries(buckets.map((b) => [b.label, 0]));

    for (const a of articles || []) {
      const b = healthBucket(getDisplayStatus(a.progress, organization?.ai_enabled));
      counts[b] = (counts[b] || 0) + 1;
    }

    const breakdown = buckets.map((b) => ({
      label: b.label,
      value: counts[b.label] || 0,
      color: b.color,
    }));

    return { breakdown };
  }, [articles, organization]);

  const activityTextForProgress = ({ title, progress, writerName }) => {
    const t = title || "Untitled";

    switch ((progress || "In Progress").trim()) {
      case "In Progress":
        return `${t} was created by ${writerName || "a writer"}.`;

      case "Edits Needed":
        return `Edits have been requested for ${t}.`;

      case "Ready for Approval":
        return `${t} has been marked ready for approval.`;

      case "Ready for Review":
        return `${t} is ready for editor review.`;

      case "Ready to Publish":
        return `${t} has been marked as ready to be published.`;

      default:
        // fallback for unexpected statuses
        return `${t} was updated.`;
    }
  };

  const recentActivity = useMemo(() => {
    const rows = (articles || [])
      .slice() // clone
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 4)
      .map((a) => {
        const writerName =
          a.Users?.name ||
          writerNameById.get(a.created_by) ||
          (a.created_by ? String(a.created_by).slice(0, 8) : "a writer");

        const displayProgress = getDisplayStatus(
          a.progress,
          organization?.ai_enabled
        );

        return {
          id: a.id,
          title: a.title || "Untitled",
          progress: displayProgress,
          updated_at: a.updated_at,
          message: activityTextForProgress({
            title: a.title,
            progress: displayProgress,
            writerName,
          }),
        };
      });

    return rows;
  }, [articles, writerNameById, organization]);

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("publish")) return styles.badgePublished;
    if (s.includes("in progress")) return styles.badgeInProgress;
    if (s.includes("review")) return styles.badgeReview;
    if (s.includes("draft")) return styles.badgeDraft;
    if (s.includes("approval")) return styles.badgePending;
    if (s.includes("edit")) return styles.badgeEdits;
    return styles.badgeDefault;
  };

  const statusOptions = useMemo(() => {
    // dropdown should reflect whatever is in DB
    const set = new Set(
      (articles || []).map((a) =>
        getDisplayStatus(a.progress, organization?.ai_enabled)
      )
    );
    return Array.from(set);
  }, [articles, organization]);

  const writerOptions = useMemo(() => {
    const set = new Set(tableRows.map((r) => r.writerName).filter(Boolean));
    return Array.from(set);
  }, [tableRows]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.headerCard}>Loading project…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.page}>
        <div style={styles.headerCard}>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>
            Failed to load project
          </div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>{loadError}</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={styles.page}>
        <div style={styles.headerCard}>Project not found.</div>
      </div>
    );
  }

  const handleGenerateSubmit = async (briefPayload) => {
    try {
      setIsGenerating(true);
      setIsGenerateOpen(false);
      setGeneratingMessage(
        "Generating your article...\nThis may take a few minutes."
      );

      // ✅ Force the article into THIS project
      const payloadWithProject = {
        ...briefPayload,
        project_id: projectId, // <-- IMPORTANT
      };

      const result = await generateContent(payloadWithProject, setFeedback);

      const generatedArticleForTextBox = {
        id: result?.id ?? null,
        title: result?.title ?? "Untitled Article",
        project_id: projectId, // <-- IMPORTANT
        content_html: result?.Article ?? result?.html ?? result?.content ?? "",
        meta_tags: result?.meta_tags ?? result?.["Meta Tags"] ?? "",
      };

      let orgId = profile?.company_id ?? null;

      // ✅ Navigate to TextBox editor so it can populate the generated content
      navigate(`/organization-portal/${orgId}/content-generation`, {
        state: { openArticle: generatedArticleForTextBox },
      });
    } catch (err) {
      console.error("Generate failed:", err);
      alert("Failed to generate article.");
    } finally {
      setIsGenerating(false);
      setGeneratingMessage("");
    }
  };

  return (
    <div style={styles.page}>
      {/* Breadcrumbs */}
      <div style={styles.breadcrumbs}>
        Dashboard &nbsp;&gt;&nbsp; Projects &nbsp;&gt;&nbsp;{" "}
        <span style={{ color: "#111827", fontWeight: 700 }}>
          {project.title}
        </span>
      </div>

      {/* Top section layout: left main + right sidebar */}
      <div style={styles.topGrid}>
        {/* LEFT HEADER */}
        <div style={styles.headerCard}>
          <div style={styles.headerTopRow}>
            <h1 style={styles.h1}>{project.title}</h1>
            <span style={styles.activePill}>Active</span>
          </div>

          <p style={styles.description}>
            {project.description?.trim()
              ? project.description
              : "No project description yet."}
          </p>

          {/* Stats */}
          <div style={styles.statsRow}>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Average Score</div>
              <div style={styles.statValue}>{stats.avgScore}</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Articles In Progress</div>
              <div style={styles.statValue}>{stats.inProgress}</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Published Articles</div>
              <div style={styles.statValue}>{stats.published}</div>
            </div>
          </div>
        </div>

        {/* RIGHT TEAM CARD */}
        <div style={styles.sideCard}>
          <div style={styles.sideTitle}>Team</div>
          <div style={styles.teamList}>
            {team.map((m) => (
              <div key={`${m.id}-${m.role}`} style={styles.teamRow}>
                {/* ✅ UPDATED: Avatar Display Logic */}
                <div style={styles.teamAvatar}>
                  {m.avatar_url ? (
                    <img
                      src={m.avatar_url}
                      alt={m.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    m.name?.[0] || "U"
                  )}
                </div>
                <div>
                  <div style={styles.teamName}>{m.name}</div>
                  <div style={styles.teamRole}>{m.role}</div>
                </div>
              </div>
            ))}
            {team.length === 0 && (
              <div style={{ color: "#6b7280", fontSize: 13 }}>
                No team members found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content grid: articles left + widgets right */}
      <div style={styles.mainGrid}>
        {/* LEFT: Project Articles */}
        <div style={styles.mainCard}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Project Articles</div>

            <button
              style={styles.newArticleButton}
              type="button"
              onClick={() => setIsGenerateOpen(true)}
            >
              New Article
            </button>
          </div>

          {/* Filters row */}
          <div style={styles.filterRow}>
            <div style={styles.searchWrap}>
              <SearchIcon />
              <input
                style={styles.searchInput}
                placeholder="Search by Title"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              style={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Filter by Status</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              style={styles.select}
              value={writerFilter}
              onChange={(e) => setWriterFilter(e.target.value)}
            >
              <option value="">Filter by Writer</option>
              {writerOptions.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          </div>

          {/* Articles table */}
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Writer</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Last Updated</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((a) => (
                <tr key={a.id} style={styles.tr}>
                  <td style={styles.tdStrong}>
                    {renamingId === a.id ? (
                      <input
                        value={renameValue}
                        autoFocus
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter") {
                            try {
                              await updateArticle(a.id, { title: renameValue });
                              setArticles((prev) =>
                                prev.map((x) =>
                                  x.id === a.id
                                    ? { ...x, title: renameValue }
                                    : x
                                )
                              );
                            } catch (err) {
                              console.error("Rename failed:", err);
                              alert("Failed to rename article.");
                            } finally {
                              setRenamingId(null);
                              setRenameValue("");
                            }
                          }
                          if (e.key === "Escape") {
                            setRenamingId(null);
                            setRenameValue("");
                          }
                        }}
                      />
                    ) : (
                      a.title
                    )}
                  </td>

                  <td style={styles.td}>{a.writerName}</td>
                  <td style={styles.td}>—</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badgeBase,
                        ...statusBadge(a.statusLabel),
                      }}
                    >
                      {a.statusLabel}
                    </span>
                  </td>
                  <td style={styles.td}>{a.updatedLabel}</td>
                  <td
                    style={{
                      ...styles.td,
                      textAlign: "right",
                      position: "relative",
                    }}
                  >
                    <button
                      style={styles.iconBtn}
                      title="Actions"
                      type="button"
                      onClick={() =>
                        setOpenMenuId((current) =>
                          current === a.id ? null : a.id
                        )
                      }
                    >
                      <DotsIcon />
                    </button>

                    {openMenuId === a.id && (
                      <div style={styles.dropdownMenu}>
                        {/* OPEN */}
                        <button
                          type="button"
                          style={styles.dropdownItem}
                          onClick={async () => {
                            try {
                              // fetch full article from Supabase
                              const full = await getArticle(a.id);

                              // ✅ go to editor page and populate TextBox using route state (same pattern as FileManagerPage)
                              navigate(
                                `/organization-portal/${project.company_id}/content-generation`,
                                {
                                  state: { openArticle: full },
                                }
                              );
                            } catch (err) {
                              console.error("Error opening article:", err);
                              alert("Failed to open article.");
                            } finally {
                              setOpenMenuId(null);
                            }
                          }}
                        >
                          Open
                        </button>

                        {/* RENAME (admins only) */}
                        {profile?.role === "Admin" && (
                          <button
                            type="button"
                            style={styles.dropdownItem}
                            onClick={() => {
                              setRenamingId(a.id);
                              setRenameValue(a.title || "");
                              setOpenMenuId(null);
                            }}
                          >
                            Rename
                          </button>
                        )}

                        {/* DELETE (admins only) */}
                        {profile?.role === "Admin" && (
                          <button
                            type="button"
                            style={styles.dropdownItemDanger}
                            onClick={async () => {
                              const confirmed = window.confirm(
                                "Are you sure you want to delete this article?"
                              );
                              if (!confirmed) return;

                              try {
                                await deleteArticle(a.id);
                                setArticles((prev) =>
                                  prev.filter((x) => x.id !== a.id)
                                );
                              } catch (err) {
                                console.error("Error deleting article:", err);
                                alert("Failed to delete the article.");
                              } finally {
                                setOpenMenuId(null);
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}

              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={6} style={styles.emptyRow}>
                    No articles match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* RIGHT WIDGETS */}
        <div style={styles.rightCol}>
          {/* Project Health */}
          <div style={styles.sideCardWide}>
            <div style={styles.sideTitle}>Project Health</div>

            <div style={styles.healthRow}>
              <DonutBreakdown breakdown={health.breakdown} />

              <div style={styles.healthLegend}>
                {health.breakdown.map((b) => (
                  <div key={b.label} style={styles.legendItem}>
                    <span
                      style={{
                        ...styles.legendDot,
                        background: b.color,
                      }}
                    />
                    <span style={styles.legendText}>
                      {b.label} ({b.value})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div style={styles.sideCardWide}>
            <div style={styles.sideTitle}>Recent Activity</div>

            <div style={styles.activityList}>
              {recentActivity.map((item) => (
                <div key={item.id} style={styles.activityRow}>
                  <div style={styles.activityTitle}>{item.title}</div>
                  <div style={styles.activityMessage}>{item.message}</div>
                </div>
              ))}

              {recentActivity.length === 0 && (
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  No recent activity yet.
                </div>
              )}
            </div>
          </div>

          {/* Project Settings */}
          <div style={styles.sideCardWide}>
            <div style={styles.sideTitle}>Project Settings</div>

            <button
              style={styles.settingsButton}
              onClick={() => setIsSettingsOpen(true)}
            >
              ⚙️&nbsp; Manage Settings
            </button>
          </div>
        </div>
      </div>
      <GenerateModal
        isOpen={isGenerateOpen}
        onClose={() => setIsGenerateOpen(false)}
        onSubmit={handleGenerateSubmit}
        currentUser={{ id: profile?.id, role: profile?.role }}
        projects={projectAsOption}
        defaultProjectId={projectId}
      />

      {isGenerating && <GeneratingOverlay message={generatingMessage} />}

      <ProjectSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        project={project}
        profile={profile}
        onProjectUpdated={(updated) => setProject(updated)}
      />
    </div>
  );
}

/* ------------------ Styles ------------------ */
const styles = {
  page: {
    padding: "28px 28px 44px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#111827",
  },
  breadcrumbs: { fontSize: 13, color: "#6b7280", marginBottom: 14 },

  topGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 300px",
    gap: 18,
    alignItems: "start",
    marginBottom: 18,
  },

  headerCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
    padding: 18,
  },

  headerTopRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
  },
  h1: { margin: 0, fontSize: 22, fontWeight: 800 },

  activePill: {
    fontSize: 12,
    fontWeight: 800,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(16, 185, 129, 0.12)",
    color: "#065f46",
    whiteSpace: "nowrap",
  },

  description: {
    marginTop: 10,
    marginBottom: 16,
    color: "#374151",
    fontSize: 13.5,
    lineHeight: 1.5,
  },

  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
  },
  statBox: {
    padding: 14,
    borderRadius: 12,
    border: "1px solid #eef2f7",
    background: "#fff",
  },
  statLabel: { fontSize: 12, color: "#6b7280", fontWeight: 700 },
  statValue: { marginTop: 6, fontSize: 18, fontWeight: 900 },

  sideCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
    padding: 16,
  },

  sideCardWide: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
    padding: 16,
  },

  sideTitle: {
    fontSize: 13,
    fontWeight: 900,
    color: "#111827",
    marginBottom: 12,
  },

  teamList: { display: "flex", flexDirection: "column", gap: 12 },
  teamRow: { display: "flex", gap: 12, alignItems: "center" },
  teamAvatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#111827",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    overflow: "hidden", // ✅ Added this to ensure image clipping
  },
  teamName: { fontWeight: 900, fontSize: 13.5 },
  teamRole: { fontSize: 12, color: "#6b7280", fontWeight: 700 },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 320px",
    gap: 18,
    alignItems: "start",
  },

  mainCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
    padding: 16,
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 14, fontWeight: 900 },

  newArticleButton: {
    background: "#00876e",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    fontWeight: 900,
    cursor: "pointer",
  },

  filterRow: {
    display: "grid",
    gridTemplateColumns: "1.2fr 0.85fr 0.85fr 1fr",
    gap: 10,
    alignItems: "center",
    marginBottom: 14,
  },

  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
  },

  searchInput: { border: "none", outline: "none", fontSize: 13, width: "100%" },

  select: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    background: "#fff",
    fontWeight: 700,
    fontSize: 13,
    color: "#111827",
    outline: "none",
  },

  scoreWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "flex-end",
  },
  scoreLabel: { fontSize: 12, color: "#6b7280", fontWeight: 800 },
  range: { width: 120 },

  table: { width: "100%", borderCollapse: "collapse" },

  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 900,
    color: "#6b7280",
    padding: "12px 12px",
    borderBottom: "1px solid #eef2f7",
    whiteSpace: "nowrap",
  },

  tr: { borderBottom: "1px solid #f1f5f9" },

  td: {
    padding: "12px 12px",
    fontSize: 13,
    color: "#111827",
    verticalAlign: "middle",
  },

  tdStrong: {
    padding: "12px 12px",
    fontSize: 13,
    fontWeight: 900,
    verticalAlign: "middle",
  },

  iconBtn: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 10,
    width: 38,
    height: 34,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyRow: {
    padding: 18,
    textAlign: "center",
    color: "#6b7280",
    fontSize: 13,
  },

  badgeBase: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  badgePublished: { background: "rgba(16,185,129,0.12)", color: "#065f46" },
  badgeInProgress: { background: "rgba(34,211,238,0.14)", color: "#0e7490" },
  badgeReview: { background: "rgba(59,130,246,0.12)", color: "#1d4ed8" },
  badgeDraft: { background: "rgba(107,114,128,0.12)", color: "#374151" },
  badgePending: { background: "rgba(245,158,11,0.16)", color: "#92400e" },
  badgeEdits: { background: "rgba(244,63,94,0.14)", color: "#9f1239" },
  badgeDefault: { background: "#f3f4f6", color: "#374151" },

  rightCol: { display: "flex", flexDirection: "column", gap: 18 },

  healthRow: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: 14,
    alignItems: "center",
  },
  healthLegend: { display: "flex", flexDirection: "column", gap: 8 },

  legendItem: { display: "flex", alignItems: "center", gap: 8 },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#10b981",
    display: "inline-block",
  },
  legendText: { fontSize: 12.5, color: "#374151", fontWeight: 800 },

  settingsButton: {
    width: "100%",
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 12,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 900,
    color: "#111827",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  menuButton: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 10,
    width: 38,
    height: 34,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  dropdownMenu: {
    position: "absolute",
    right: 0,
    top: "100%",
    marginTop: 6,
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
    padding: "4px 0",
    minWidth: 140,
    zIndex: 9999,
    border: "1px solid #e5e7eb",
  },

  dropdownItem: {
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    padding: "8px 12px",
    fontSize: 14,
    color: "#111827",
    cursor: "pointer",
  },

  dropdownItemDanger: {
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    padding: "8px 12px",
    fontSize: 14,
    color: "#b91c1c",
    cursor: "pointer",
  },

  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  activityRow: {
    border: "1px solid #eef2f7",
    background: "#fff",
    borderRadius: 12,
    padding: "10px 12px",
  },

  activityTitle: {
    fontSize: 13,
    fontWeight: 900,
    color: "#111827",
    marginBottom: 4,
  },

  activityMessage: {
    fontSize: 13,
    color: "#6b7280",
    fontWeight: 700,
    lineHeight: 1.3,
  },
};
